import React, { useRef, useState } from "react";
import { formatClock, formatLockDate } from "../../hooks/useClock";
import { ReactComponent as FlashlightOff } from "../../assets/svgs/sf-symbols/ios/flashlight.off.fill.svg";
import { ReactComponent as FlashlightOn } from "../../assets/svgs/sf-symbols/ios/flashlight.on.fill.svg";
import { ReactComponent as Camera } from "../../assets/svgs/sf-symbols/ios/camera.fill.svg";

const UNLOCK = 0.18; // a drag unlocks once it covers this much of the screen...
const FLICK = 0.5; // ...or moves up faster than this (px per ms)

// The year so far, as thelifecalendar.com's "Days" wallpaper counts it: today is day `day` of
// `total`, `left` days follow it, and `percent` of the year is done.
const yearProgress = (now) => {
  const year = now.getFullYear();
  const total = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
  const day = (Date.UTC(year, now.getMonth(), now.getDate()) - Date.UTC(year, 0, 1)) / 86400000 + 1;
  return { day, total, left: total - day, percent: Math.floor((day / total) * 100) };
};

// The Lock Screen, like Sylvester's: his year-in-dots wallpaper (one dot per day: white for the
// days gone, orange for today), the date and the glass clock, the flashlight and camera buttons.
// Swipe up (or drag up with a mouse, scroll up, or tap the bar at the bottom) to unlock;
// `onProgress` reports how far a drag has gone (0 to 1), so the Home Screen can show through.
const LockScreen = ({ now, leaving, onProgress, onUnlock }) => {
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [torch, setTorch] = useState(false);
  const start = useRef(null);
  const wheel = useRef(0);
  const { day, total, left, percent } = yearProgress(now);

  const follow = (dy) => {
    setDrag(dy);
    onProgress(Math.min(1, -dy / (window.innerHeight * 0.6)));
  };

  const onPointerDown = (e) => {
    if (leaving || e.button > 0) return;
    start.current = { id: e.pointerId, y: e.clientY, t: e.timeStamp, moving: false };
  };

  const onPointerMove = (e) => {
    const s = start.current;
    if (!s || e.pointerId !== s.id) return;
    const dy = Math.min(0, e.clientY - s.y);
    if (!s.moving && dy < -6) {
      s.moving = true;
      e.currentTarget.setPointerCapture(s.id);
      setDragging(true);
    }
    if (s.moving) follow(dy);
  };

  const onPointerUp = (e) => {
    const s = start.current;
    start.current = null;
    if (!s || !s.moving) return;
    setDragging(false);
    const dy = Math.min(0, e.clientY - s.y);
    const speed = dy / Math.max(1, e.timeStamp - s.t);
    if (dy < -window.innerHeight * UNLOCK || speed < -FLICK) onUnlock();
    else follow(0);
  };

  // A trackpad's two-finger swipe up arrives as wheel scrolling.
  const onWheel = (e) => {
    wheel.current = Math.max(0, wheel.current + e.deltaY);
    if (wheel.current > 60 && !leaving) onUnlock();
  };

  return (
    <div
      className={`lock ${dragging ? "is-dragging" : ""} ${leaving ? "is-leaving" : ""}`}
      style={{ "--drag": `${drag}px` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      role="region"
      aria-label="Lock Screen"
    >
      <div className="lock-inner">
        <p className="lock-date">{formatLockDate(now)}</p>
        <p className="lock-time" aria-label={`Time ${formatClock(now)}`}>
          {formatClock(now)}
        </p>

        <div className="lock-dots" role="img" aria-label={`Day ${day} of ${total}`}>
          {Array.from({ length: total }, (_, i) => (
            <i key={i} className={i + 1 < day ? "is-past" : i + 1 === day ? "is-today" : ""} />
          ))}
        </div>
        <p className="lock-caption">
          <span className="lock-left">{left}d left</span>
          <span className="lock-percent"> · {percent}%</span>
        </p>

        <button
          type="button"
          className={`lock-button lock-button--torch ${torch ? "is-on" : ""}`}
          onClick={() => setTorch(!torch)}
          aria-label="Flashlight"
          aria-pressed={torch}
        >
          {torch ? <FlashlightOn className="lock-glyph-torch" /> : <FlashlightOff className="lock-glyph-torch" />}
        </button>
        <span className="lock-button lock-button--camera" aria-hidden="true">
          <Camera className="lock-glyph-camera" />
        </span>

        <button type="button" className="lock-unlock" onClick={onUnlock} aria-label="Unlock">
          <span className="lock-hint">Swipe up to open</span>
          <span className="lock-bar" />
        </button>
      </div>
    </div>
  );
};

export default LockScreen;
